import {
  ChangeDetectionStrategy,
  Component,
  ComponentFactory,
  ComponentRef,
  ChangeDetectorRef,
  EmbeddedViewRef,
  EventEmitter,
  Injector,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
  ViewContainerRef,
  TemplateRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { KillOnDestroy } from '@sg/utils';

import { SgAuxRouteBridgeHost } from '../models';
import { SgAuxRouterBridgeHostRouteChangedEvent } from '../events';
import { SgAuxRouteBridgeService } from '../aux-route-bridge.service';
import { SgAuxRouteRef } from '../aux-route-ref';
import { SgAuxRouter } from '../aux-router.service';

const SG_AUX_ROUTE_PATH_PARAM = '__SG_AUX_ROUTE_PATH_PARAM__';
export const SG_AUX_ROUTE_PATH = ':' + SG_AUX_ROUTE_PATH_PARAM;

@Component({
  selector: 'sg-aux-route-bridge-host',
  template: '',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
@KillOnDestroy()
export class SgAuxRouteBridgeHostComponent implements SgAuxRouteBridgeHost, OnInit, OnDestroy {
  readonly outlet: string;

  get activeViewRef(): EmbeddedViewRef<any> | undefined {
    return this.viewRef;
  }
  get activeCmpRef(): ComponentRef<any> | undefined {
    return this.cmpRef;
  }
  get activePath(): string {
    return this.lastParams && this.lastParams[SG_AUX_ROUTE_PATH_PARAM];
  }

  @Output() routeChanged = new EventEmitter<SgAuxRouterBridgeHostRouteChangedEvent>();
  @Output() onDestroy = new EventEmitter<void>();

  private cmpRef?: ComponentRef<any>;
  private viewRef?: EmbeddedViewRef<any>;
  private lastParams: any = {};

  constructor(
    private vcRef: ViewContainerRef,
    private cdr: ChangeDetectorRef,
    private injector: Injector,
    private auxRouter: SgAuxRouter,
    private route: ActivatedRoute,
    private bridge: SgAuxRouteBridgeService
  ) {
    this.outlet = route.outlet;
    bridge.registerHost(this);
  }

  createComponent<T = any>(cmpFactory: ComponentFactory<T>): ComponentRef<T> {
    this.clearView();
    this.viewRef = undefined;

    const outlet = this.bridge.getOutlet(this.outlet);
    const auxRefFactory = SgAuxRouteRef.create(this.cdr, this.route, this.auxRouter, outlet);

    this.cmpRef = this.vcRef.createComponent(cmpFactory, 0, this.createInjector(auxRefFactory.auxRef));
    auxRefFactory.update(this.cmpRef);

    // Manually trigger change detection for OnPush components
    this.cmpRef.changeDetectorRef.detectChanges();

    return this.cmpRef;
  }

  createView<T = any>(tRef: TemplateRef<T>, ctx?: T): EmbeddedViewRef<T> {
    this.clearView();
    this.cmpRef = undefined;
    return (this.viewRef = this.vcRef.createEmbeddedView(tRef, ctx));
  }

  clearView(): void {
    this.vcRef.clear();
  }

  ngOnInit() {
    this.route.params.pipe(KillOnDestroy(this)).subscribe((routeParams) => {
      const lastParams = this.lastParams;
      this.lastParams = routeParams;

      const params = { ...routeParams };
      const path = params[SG_AUX_ROUTE_PATH_PARAM];
      delete params[SG_AUX_ROUTE_PATH_PARAM];

      const event: SgAuxRouterBridgeHostRouteChangedEvent = {
        host: this,
        path,
        params,
        pathChanged: path !== lastParams[SG_AUX_ROUTE_PATH_PARAM],
      };
      this.routeChanged.next(event);
    });
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
    this.routeChanged.complete();
  }

  protected createInjector(auxRouteRef: SgAuxRouteRef): Injector {
    return Injector.create({
      providers: [
        { provide: ActivatedRoute, useValue: this.route },
        { provide: SgAuxRouteRef, useValue: auxRouteRef },
      ],
      parent: this.injector,
    });
  }
}
